import { type JSX, useEffect, useState } from 'react';

import {
  createBroadcast,
  type CreateBroadcastResult,
  deleteBroadcast,
  listBroadcasts,
  type PortalBroadcast,
} from '../api.js';
import { confirmDialog } from './dialogs.js';

export interface BroadcastsEditorProps {
  tenantId: string;
  /** Owner bearer; null for a non-owner admin (writes via the session cookie). */
  editToken: string | null;
  /** Viewer mode — hide the composer + delete, show the broadcast list only. */
  readOnly?: boolean;
}

type Severity = PortalBroadcast['severity'];

interface ComposerState {
  title: string;
  body: string;
  severity: Severity;
  /** Local datetime-input value (yyyy-mm-ddThh:mm); empty = "now". */
  startsAtInput: string;
  /** Local datetime-input value. Required. */
  expiresAtInput: string;
  dismissible: boolean;
}

const DEFAULT_DURATION_HOURS = 24;

function emptyComposer(): ComposerState {
  return {
    title: '',
    body: '',
    severity: 'info',
    startsAtInput: '',
    expiresAtInput: defaultExpiresInput(DEFAULT_DURATION_HOURS),
    dismissible: true,
  };
}

/**
 * Customer-managed broadcasts editor. Lists every broadcast on the
 * tenant (active + scheduled + expired) and provides a compose form.
 * Reaches the licensing-server with the tenant's editToken — same auth
 * surface as the config editor next door.
 */
export function BroadcastsEditor({
  tenantId,
  editToken,
  readOnly = false,
}: BroadcastsEditorProps): JSX.Element {
  const [rows, setRows] = useState<PortalBroadcast[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [composer, setComposer] = useState<ComposerState>(() => emptyComposer());
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionOk, setActionOk] = useState<string | null>(null);
  // Suppress validation messages until the user has touched the form
  // OR pressed Post — initial render shouldn't shout "Title is required."
  const [showValidation, setShowValidation] = useState(false);

  function updateComposer(patch: Partial<ComposerState>): void {
    setShowValidation(true);
    setComposer((prev) => ({ ...prev, ...patch }));
  }

  async function refresh(): Promise<void> {
    setLoadError(null);
    const res = await listBroadcasts({ tenantId, editToken });
    if (res.kind === 'ok') {
      setRows(res.broadcasts);
      return;
    }
    if (res.kind === 'unauthorized') {
      setLoadError('Edit token rejected. Re-sign up with the same email to recover.');
      return;
    }
    if (res.kind === 'network') {
      setLoadError(`Network: ${res.message}`);
      return;
    }
    setLoadError(`Server returned ${String(res.status)}`);
  }

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const res = await listBroadcasts({ tenantId, editToken });
      if (cancelled) return;
      if (res.kind === 'ok') {
        setRows(res.broadcasts);
      } else if (res.kind === 'unauthorized') {
        setLoadError('Edit token rejected. Re-sign up with the same email to recover.');
      } else if (res.kind === 'network') {
        setLoadError(`Network: ${res.message}`);
      } else {
        setLoadError(`Server returned ${String(res.status)}`);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId, editToken]);

  const validation = validateComposer(composer);

  async function handleCreate(): Promise<void> {
    setShowValidation(true);
    if (validation !== null) return;
    setBusy(true);
    setActionError(null);
    setActionOk(null);
    const startsAt = composer.startsAtInput.trim()
      ? localInputToUnixSeconds(composer.startsAtInput)
      : undefined;
    const expiresAt = localInputToUnixSeconds(composer.expiresAtInput);
    const args = {
      tenantId,
      editToken,
      title: composer.title.trim(),
      body: composer.body.trim(),
      severity: composer.severity,
      dismissible: composer.dismissible,
      expiresAt,
      ...(startsAt !== undefined && { startsAt }),
    };
    const res: CreateBroadcastResult = await createBroadcast(args);
    setBusy(false);
    if (res.kind === 'ok') {
      setActionOk(`Broadcast “${res.broadcast.title}” created.`);
      setComposer(emptyComposer());
      // Reset validation suppression too — without this the freshly
      // empty title field would immediately fail validation since the
      // user just clicked Post (which set showValidation=true).
      setShowValidation(false);
      await refresh();
      return;
    }
    if (res.kind === 'unauthorized') {
      setActionError('Edit token rejected.');
      return;
    }
    if (res.kind === 'invalid') {
      setActionError(`Server rejected: ${res.message}`);
      return;
    }
    if (res.kind === 'network') {
      setActionError(`Network: ${res.message}`);
      return;
    }
    setActionError(`Server returned ${String(res.status)}: ${res.message}`);
  }

  async function handleDelete(id: string, title: string): Promise<void> {
    const ok = await confirmDialog({
      title: 'Delete broadcast?',
      message: `Delete "${title}"? It will stop showing on devices at their next poll. This can't be undone.`,
      confirmLabel: 'Delete broadcast',
      destructive: true,
      requireText: 'delete',
    });
    if (!ok) return;
    setActionError(null);
    setActionOk(null);
    const res = await deleteBroadcast({ tenantId, editToken, id });
    if (res.kind === 'ok' || res.kind === 'not-found') {
      setActionOk('Broadcast deleted.');
      await refresh();
      return;
    }
    if (res.kind === 'unauthorized') {
      setActionError('Edit token rejected.');
      return;
    }
    if (res.kind === 'network') {
      setActionError(`Network: ${res.message}`);
      return;
    }
    setActionError(`Server returned ${String(res.status)}`);
  }

  return (
    <div className="broadcasts-editor" data-testid="broadcasts-editor">
      <div className="broadcasts-editor__columns">
        {!readOnly && (
        <section className="broadcasts-editor__col broadcasts-editor__col--compose">
          <header className="broadcasts-editor__head">
            <h3 className="broadcasts-editor__heading">Compose a broadcast</h3>
            <p className="broadcasts-editor__sub">
              Devices poll every 60 seconds — new broadcasts show up in the bell-icon panel within a minute.
            </p>
          </header>
          <div className="broadcasts-editor__form">
        <label className="lookup__field">
          <span className="lookup__label">Title (max 80)</span>
          <input
            className="lookup__input"
            type="text"
            value={composer.title}
            maxLength={80}
            onChange={(e) => updateComposer({ title: e.target.value })}
            data-testid="broadcasts-title"
          />
        </label>
        <label className="lookup__field">
          <span className="lookup__label">Message (max 500)</span>
          <textarea
            className="lookup__input"
            rows={3}
            maxLength={500}
            value={composer.body}
            onChange={(e) => updateComposer({ body: e.target.value })}
            data-testid="broadcasts-body"
          />
        </label>
        <div className="editor__row-pair">
          <label className="lookup__field">
            <span className="lookup__label">Severity</span>
            <select
              className="lookup__input"
              value={composer.severity}
              onChange={(e) =>
                updateComposer({ severity: e.target.value as Severity })
              }
            >
              <option value="info">Info</option>
              <option value="warning">Warning</option>
              <option value="critical">Critical</option>
            </select>
          </label>
          <label className="lookup__field">
            <span className="lookup__label">Dismissible</span>
            <select
              className="lookup__input"
              value={composer.dismissible ? 'yes' : 'no'}
              onChange={(e) =>
                updateComposer({ dismissible: e.target.value === 'yes' })
              }
            >
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </label>
        </div>
        <div className="editor__row-pair">
          <label className="lookup__field">
            <span className="lookup__label">Starts at (optional — default: now)</span>
            <input
              className="lookup__input"
              type="datetime-local"
              value={composer.startsAtInput}
              onChange={(e) => updateComposer({ startsAtInput: e.target.value })}
            />
          </label>
          <label className="lookup__field">
            <span className="lookup__label">Expires at (required)</span>
            <input
              className="lookup__input"
              type="datetime-local"
              value={composer.expiresAtInput}
              onChange={(e) => updateComposer({ expiresAtInput: e.target.value })}
              data-testid="broadcasts-expires"
            />
          </label>
        </div>
        {showValidation && validation !== null && (
          <p className="details__bundle-err" role="alert">{validation}</p>
        )}
        {actionError !== null && (
          <p className="details__bundle-err" role="alert">{actionError}</p>
        )}
        {actionOk !== null && (
          <p className="details__bundle-ok" role="status">{actionOk}</p>
        )}
            <div className="details__actions">
              <button
                type="button"
                className="details__bundle"
                disabled={busy}
                onClick={() => {
                  void handleCreate();
                }}
                data-testid="broadcasts-create"
              >
                {busy ? 'Posting…' : 'Post broadcast'}
              </button>
            </div>
          </div>
        </section>
        )}

        <section className="broadcasts-editor__col broadcasts-editor__col--list">
          <header className="broadcasts-editor__head">
            <h3 className="broadcasts-editor__heading">All broadcasts</h3>
            <p className="broadcasts-editor__sub">
              Active, scheduled, and recently expired. Delete removes immediately on the next device poll.
            </p>
          </header>
          <div className="broadcasts-editor__list-wrap">
            {loadError !== null && (
              <p className="details__bundle-err" role="alert">{loadError}</p>
            )}
            {rows === null && loadError === null && (
              <p className="details__hint">Loading broadcasts…</p>
            )}
            {rows !== null && rows.length === 0 && (
              <p className="details__hint">No broadcasts yet.</p>
            )}
            {rows !== null && rows.length > 0 && (
              <ul className="editor__tile-list">
                {rows.map((b) => (
                  <BroadcastRow
                    key={b.id}
                    broadcast={b}
                    onDelete={
                      readOnly
                        ? undefined
                        : () => {
                            void handleDelete(b.id, b.title);
                          }
                    }
                  />
                ))}
              </ul>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

function BroadcastRow({
  broadcast,
  onDelete,
}: {
  broadcast: PortalBroadcast;
  /** Undefined in read-only (viewer) mode — the delete control is hidden. */
  onDelete?: (() => void) | undefined;
}): JSX.Element {
  const now = Math.floor(Date.now() / 1000);
  let status: string;
  if (broadcast.expiresAt <= now) status = 'expired';
  else if (broadcast.startsAt > now) status = 'scheduled';
  else status = 'active';
  return (
    <li className="editor__tile" data-testid={`broadcasts-row-${broadcast.id}`}>
      <div className="editor__tile-row">
        <span className={`details__tier-badge details__tier-badge--${badgeTone(broadcast.severity)}`}>
          {broadcast.severity}
        </span>
        <span className="editor__tile-label" style={{ flex: 1 }}>
          <strong>{broadcast.title}</strong>
          <br />
          <small>
            {status} · expires {new Date(broadcast.expiresAt * 1000).toLocaleString()}
          </small>
        </span>
        {onDelete && (
          <button
            type="button"
            className="editor__tile-remove"
            onClick={onDelete}
            aria-label="Delete broadcast"
            title="Delete"
            data-testid={`broadcasts-delete-${broadcast.id}`}
          >
            ×
          </button>
        )}
      </div>
      <p
        className="editor__tile-target"
        style={{ whiteSpace: 'pre-wrap', margin: 0, fontSize: '0.9rem' }}
      >
        {broadcast.body}
      </p>
    </li>
  );
}

function badgeTone(severity: Severity): string {
  // Use severity-named classes so the pill colors match the conventional
  // info=blue / warning=amber / critical=red language. Reusing tier
  // colors here was misleading (a blue "WARNING" pill looked like a
  // Pro-tier badge).
  return `severity-${severity}`;
}

function validateComposer(c: ComposerState): string | null {
  if (c.title.trim().length === 0) return 'Title is required.';
  if (c.body.trim().length === 0) return 'Message is required.';
  if (c.expiresAtInput.trim().length === 0) return 'Expires-at is required.';
  const expiresAt = localInputToUnixSeconds(c.expiresAtInput);
  const startsAt = c.startsAtInput.trim()
    ? localInputToUnixSeconds(c.startsAtInput)
    : Math.floor(Date.now() / 1000);
  if (expiresAt <= startsAt) return 'Expires-at must be after starts-at.';
  return null;
}

/** Convert an `<input type="datetime-local">` value (local TZ) to unix seconds. */
function localInputToUnixSeconds(input: string): number {
  // datetime-local has no timezone — interpret as local time. The
  // Date constructor without a TZ does exactly that.
  const t = new Date(input).getTime();
  return Math.floor(t / 1000);
}

/** Local-formatted yyyy-mm-ddThh:mm for the datetime-local default. */
function defaultExpiresInput(hoursFromNow: number): string {
  const d = new Date(Date.now() + hoursFromNow * 60 * 60 * 1000);
  const pad = (n: number): string => String(n).padStart(2, '0');
  const yyyy = String(d.getFullYear());
  const mm = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  const hh = pad(d.getHours());
  const mi = pad(d.getMinutes());
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
}
